/**
 * AccountField.tsx — the contact surfaces' Company field (§25 L-1 + L-4): the
 * SAME AccountPicker the deal forms use, mounted pick-only (no create row,
 * P1-1) and clearable (L-2), plus the near-match suggestion tile under it —
 * the Slice C chooser fed by /matches?company= on blur, offering "Use this
 * account" (a toggle: the picked candidate reads "Selected"). One component,
 * three mounts: New Contact, the contact card's editor, the lead drawer.
 */
import { useState } from 'react'
import { AccountPicker, type AccountPick } from './AccountPicker'
import { AdvisoryChooser, type CandidateAction } from './DuplicateChooser'
import { fetchSalesMatches, useAsync } from '../../api'
import type { SalesMatchCandidate } from '../../../../api/types'
import { ACCOUNT_SUGGESTION_COPY, pickFromCandidate, suggestionQuery } from './accountSuggestion'

export function AccountField({ value, onChange, className, id, placeholder, autoFocus }: {
  value: AccountPick
  onChange: (next: AccountPick) => void
  className: string
  id?: string
  placeholder?: string
  autoFocus?: boolean
}) {
  // Queried on BLUR, never per keystroke (the C2.2 discipline), and only
  // for free text with no pick behind it (suggestionQuery). Since the L-9
  // recon it is ALSO asked once at mount for an editor that opens on unlinked
  // text (Cindy's "AstraZeneca", Kate's "Shell"): the tile then says at rest
  // what the field can offer — or that nothing matches — instead of waiting
  // for a blur nobody knew to give. An empty field (New Contact) asks nothing.
  const [query, setQuery] = useState(() => suggestionQuery(value))
  const matches = useAsync(
    () => (query === '' ? Promise.resolve([] as SalesMatchCandidate[]) : fetchSalesMatches({ company: query })),
    [query],
  )
  const candidates = (matches.data ?? []).filter(c => c.type === 'account')
  const actionFor = (c: SalesMatchCandidate): CandidateAction | undefined => {
    if (c.type !== 'account') return undefined
    const pick = pickFromCandidate(c)
    return {
      label: 'Use this account', pressedLabel: 'Selected',
      pressed: value.accountId !== '' && value.accountId === pick.accountId,
      onAct: () => { onChange(pick); setQuery('') },
    }
  }
  return (
    <div className="flex flex-col gap-2">
      <AccountPicker id={id} value={value} onChange={onChange} className={className} placeholder={placeholder}
        autoFocus={autoFocus} allowCreate={false} clearable
        onBlur={() => setQuery(suggestionQuery(value))} />
      {query !== '' && (
        <AdvisoryChooser loading={matches.loading} error={matches.error} candidates={candidates}
          actionFor={actionFor} copy={ACCOUNT_SUGGESTION_COPY} />
      )}
    </div>
  )
}
